/**
 * First-run activation wizard — the concept->live guided journey a brand-new org's
 * founder runs AFTER OnboardingGate (the personality quiz). Four steps, each wired to
 * a REAL endpoint (no stubs): create-first-project, connect-domain, invite-team,
 * meet-Felix. Progress persists to organizations.settings->'activation' via
 * activationApi, and each transition emits an activation.* funnel event.
 *
 * This is a SOFT gate: every step is skippable to its real empty-state surface, and the
 * whole wizard can be dismissed ("I'll do this later"). The hard-gate variant (block the
 * app until a project exists) is a one-line branch in ActivationGate — see that file —
 * pending Bob's decision. The hard requirement the story sets is that a brand-new org can
 * reach a created project purely through this wizard, which the project step satisfies.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { activationApi, EMPTY_ACTIVATION_PROGRESS } from "@/api/activationApi";
import type { ActivationProgress, ActivationStepKey } from "@/api/activationApi";
import { projectsApi } from "@/api/services";
import { rolesApi, usersApi } from "@/api/services";
import { useFelixPanelStore } from "@/stores/felixPanel";
import { recordTelemetry } from "@/lib/telemetry";
import { planLimitInfo } from "@/lib/felixRefusal";
import { DOMAIN_RESALE_IN_SCOPE } from "@/lib/productScope";

// The domain step hands off to a registrar checkout. While domain resale is out
// of scope that checkout can only refuse, so the step is not offered at all.
const STEP_ORDER: ActivationStepKey[] = (
  ["project", "domain", "team", "felix"] as ActivationStepKey[]
).filter((key) => key !== "domain" || DOMAIN_RESALE_IN_SCOPE);

const STEP_META: Record<ActivationStepKey, { title: string; blurb: string }> = {
  project: {
    title: "Create your first project",
    blurb: "This is where Felix and your team do the work. Give it a name to begin.",
  },
  domain: {
    title: "Connect a domain",
    blurb: "Point a domain at your project so it's ready to go live. Optional for now.",
  },
  team: {
    title: "Invite your team",
    blurb: "Bring the people who'll build with you. They'll get an email invite.",
  },
  felix: {
    title: "Meet Felix",
    blurb: "Felix is your AI program manager. Say hello and tell Felix what you're building.",
  },
};

interface Props {
  /** Called when the wizard is completed or dismissed (gate hides it). */
  onDone?: () => void;
}

export function ActivationWizard({ onDone }: Props) {
  const navigate = useNavigate();
  const [progress, setProgress] = useState<ActivationProgress>(EMPTY_ACTIVATION_PROGRESS);
  const [loaded, setLoaded] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  // NO DEFAULT ROLE (row c1b23e5d): the founder chooses the teammate's role,
  // and the server refuses an invite that names none.
  const [inviteRoleId, setInviteRoleId] = useState("");
  const [inviteRoles, setInviteRoles] = useState<{ id: string; display_name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load persisted progress once; announce the wizard start to the funnel.
  useEffect(() => {
    let active = true;
    activationApi
      .getProgress()
      .then((p) => {
        if (!active) return;
        setProgress(p ?? EMPTY_ACTIVATION_PROGRESS);
      })
      .catch(() => {
        /* fall back to the empty default — never block the user */
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    void recordTelemetry("activation.wizard_started");
    return () => {
      active = false;
    };
  }, []);

  // The active step = first step that is neither done nor skipped.
  const activeStep = useMemo<ActivationStepKey | null>(() => {
    for (const key of STEP_ORDER) {
      const st = progress.steps[key];
      if (!st?.done && !st?.skipped) return key;
    }
    return null;
  }, [progress.steps]);

  useEffect(() => {
    if (loaded && activeStep) {
      void recordTelemetry("activation.step_viewed", { step: activeStep });
    }
  }, [loaded, activeStep]);

  // The org's roles, for the team step's role picker. Loaded when that step is
  // on screen; a failure leaves the picker empty and the invite refused with a
  // sentence, never a crash.
  useEffect(() => {
    if (activeStep !== "team") return;
    let active = true;
    Promise.resolve()
      .then(() => rolesApi.list())
      .then((r) => {
        if (active) setInviteRoles(r?.items ?? []);
      })
      .catch(() => {
        /* the picker stays empty; inviteTeam explains */
      });
    return () => {
      active = false;
    };
  }, [activeStep]);

  const persist = useCallback((next: ActivationProgress) => {
    setProgress(next);
    // Fire-and-forget; the UI already reflects the change optimistically.
    activationApi.putProgress(next).catch(() => {
      /* persistence is best-effort; the derived resources are the source of truth */
    });
  }, []);

  const markStep = useCallback(
    (key: ActivationStepKey, patch: { done?: boolean; skipped?: boolean; project_id?: string }) => {
      const next: ActivationProgress = {
        ...progress,
        current_step: key,
        steps: { ...progress.steps, [key]: { ...progress.steps[key], ...patch } },
      };
      persist(next);
      if (patch.done) void recordTelemetry("activation.step_completed", { step: key });
      if (patch.skipped) void recordTelemetry("activation.step_skipped", { step: key });
    },
    [progress, persist],
  );

  const finish = useCallback(
    (dismissed: boolean) => {
      const next: ActivationProgress = {
        ...progress,
        dismissed,
        completed_at: new Date().toISOString(),
      };
      persist(next);
      void recordTelemetry(dismissed ? "activation.dismissed" : "activation.completed");
      onDone?.();

      // Re-apply the Felix intent. `meetFelix` sets the panel open, but that
      // store is in-memory: it persists the panel WIDTH to localStorage and not
      // `open`, so it defaults closed on every load. A founder who clicked
      // "Meet Felix" and then reloaded came back with steps.felix already
      // {done: true} and the panel shut — the journey recorded a step that
      // never happened for them, which is worse than not recording it. Setting
      // it here means the intent survives a reload, because the step does.
      if (!dismissed && progress.steps.felix?.done) {
        useFelixPanelStore.getState().setOpen(true);
      }

      // Hand off to the domain selector the user asked for in step 2. Deferred
      // to here because that is the first moment the gate stops rendering this
      // wizard in place of the routed page — navigating any earlier changes the
      // URL behind an unchanged screen. A dismissal skips the hand-off: someone
      // who bailed out did not ask to go anywhere.
      const projectId = progress.steps.project?.project_id;
      const wantsDomain = DOMAIN_RESALE_IN_SCOPE && !dismissed && progress.steps.domain?.done;
      navigate(wantsDomain && projectId ? `/projects/${projectId}/domain` : "/");
    },
    [progress, persist, onDone, navigate],
  );

  const createProject = useCallback(async () => {
    if (!projectName.trim()) {
      setError("Give your project a name.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await projectsApi.create({
        name: projectName.trim(),
        // NO status. Deliberately.
        //
        // This sent "PLANNING", which migration 529 RETIRED — the registered
        // project statuses are now INITIATING, ACTIVE, ON_HOLD, COMPLETED,
        // CANCELLED, ARCHIVED. Nothing would have caught it: `projects` has no
        // CHECK or FK on status (verified against pg_constraint), and
        // POST /projects builds its INSERT straight from body.model_dump(), so
        // an unregistered value is stored verbatim.
        //
        // The consequence lands on the WORST possible person. This wizard runs
        // once for every new signup, and the projects list filters on those six
        // conditions — so a brand-new customer's very first project would match
        // no filter and simply not appear. It is latent only because no signup
        // has completed activation since 529 landed; the next one triggers it.
        //
        // Omitting the field rather than sending "INITIATING" is the point: the
        // column already defaults to INITIATING, so the vocabulary lives in ONE
        // place. This file previously had to be edited for the title-case fix
        // (migration 528) and would have needed editing again for 529 — a
        // client that restates the server's vocabulary has to be chased every
        // time it changes, and this one was missed.
      });
      void recordTelemetry("activation.project_created");
      markStep("project", { done: true, project_id: created?.id });
      setProjectName("");
    } catch (err) {
      // "Please try again" is an UNRESOLVABLE INSTRUCTION for a plan limit:
      // retrying is the one thing guaranteed not to work, and this is step 1 of
      // 4 in activation, so the customer is stuck at the front door being told
      // to do something futile. Since the project cap shipped, a signup into an
      // org already at its limit lands exactly here.
      //
      // planLimitInfo() is the same helper FelixChat and ProjectLanding use, so
      // the wording is identical wherever a customer meets the wall, and the
      // server's own sentence is preferred over anything invented here.
      const limit = planLimitInfo(err);
      if (limit) {
        const counts =
          limit.current !== undefined && limit.limit !== undefined
            ? ` You're at ${limit.current} of ${limit.limit}.`
            : "";
        setError(`${limit.message}${counts}`);
      } else {
        setError("Couldn't create the project. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }, [projectName, markStep]);

  const goToDomain = useCallback(() => {
    void recordTelemetry("activation.domain_started");
    // Record the intent and advance — do NOT navigate yet.
    //
    // ActivationGate renders this wizard IN PLACE of the routed children, so
    // navigating while it is open changes the URL behind an unchanged screen:
    // the domain selector never appears and the button reads as dead. Before
    // the gate learned to resume, the navigation "worked" only by accident —
    // it unmounted the gate, which abandoned the journey at step 2 and never
    // showed the team or Felix steps at all.
    //
    // So the hand-off waits for `finish`, which is the moment the wizard gives
    // the screen back. The user still lands on the domain selector; they land
    // there having completed all four steps instead of two.
    markStep("domain", { done: true });
  }, [markStep]);

  const inviteTeam = useCallback(async () => {
    const email = inviteEmail.trim();
    if (!email || !email.includes("@")) {
      setError("Enter a valid email to invite.");
      return;
    }
    if (!inviteRoleId) {
      setError("Choose a role for your teammate.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await usersApi.invite({ email, role_id: inviteRoleId });
      void recordTelemetry("activation.team_invited");
      markStep("team", { done: true });
      setInviteEmail("");
      setInviteRoleId("");
    } catch {
      setError("Couldn't send the invite. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [inviteEmail, inviteRoleId, markStep]);

  const meetFelix = useCallback(() => {
    // Opening here is harmless but not load-bearing — FelixChat mounts inside
    // AppShell, which the gate renders IN PLACE of while the wizard is open, so
    // nothing is visible until `finish` gives the screen back. The durable
    // record is the step itself; `finish` re-applies it. See the note there.
    useFelixPanelStore.getState().setOpen(true);
    void recordTelemetry("activation.felix_met");
    markStep("felix", { done: true });
  }, [markStep]);

  const doneCount = STEP_ORDER.filter(
    (k) => progress.steps[k]?.done || progress.steps[k]?.skipped,
  ).length;
  const allDone = activeStep === null;

  return (
    <div className="mx-auto max-w-2xl px-6 py-10" data-testid="activation-wizard">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Let's get you set up
        </h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Four quick steps to go from empty account to a live project with Felix.
        </p>
        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
          <div
            className="h-full rounded-full bg-indigo-600 transition-all"
            style={{ width: `${(doneCount / STEP_ORDER.length) * 100}%` }}
            data-testid="activation-progress-bar"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <ol className="space-y-3">
        {STEP_ORDER.map((key) => {
          const st = progress.steps[key];
          const isActive = key === activeStep;
          const complete = st?.done || st?.skipped;
          return (
            <li
              key={key}
              data-step={key}
              data-complete={complete ? "true" : "false"}
              className={`rounded-xl border p-4 transition ${
                isActive
                  ? "border-indigo-400 bg-white shadow-sm dark:border-indigo-500 dark:bg-slate-900"
                  : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50"
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                    st?.done
                      ? "bg-green-600 text-white"
                      : st?.skipped
                        ? "bg-slate-400 text-white"
                        : "bg-indigo-600 text-white"
                  }`}
                >
                  {st?.done ? "✓" : st?.skipped ? "–" : STEP_ORDER.indexOf(key) + 1}
                </span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {STEP_META[key].title}
                </span>
              </div>

              {isActive && (
                <div className="mt-3 pl-9">
                  <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
                    {STEP_META[key].blurb}
                  </p>

                  {key === "project" && (
                    <div className="flex flex-wrap gap-2">
                      <input
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="e.g. Acme Mobile App"
                        className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                        data-testid="activation-project-name"
                      />
                      <button
                        onClick={createProject}
                        disabled={busy}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                        data-testid="activation-create-project"
                      >
                        {busy ? "Creating…" : "Create project"}
                      </button>
                    </div>
                  )}

                  {key === "domain" && (
                    <div className="flex gap-2">
                      <button
                        onClick={goToDomain}
                        data-testid="activation-domain"
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                      >
                        Connect a domain
                      </button>
                      <button
                        onClick={() => markStep("domain", { skipped: true })}
                        className="rounded-md px-4 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      >
                        Add one later
                      </button>
                    </div>
                  )}

                  {key === "team" && (
                    <div className="flex flex-wrap gap-2">
                      <input
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="teammate@company.com"
                        className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                        data-testid="activation-invite-email"
                      />
                      <select
                        value={inviteRoleId}
                        onChange={(e) => setInviteRoleId(e.target.value)}
                        aria-label="Role"
                        data-testid="activation-invite-role"
                        className="rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
                      >
                        <option value="" disabled>
                          Choose a role
                        </option>
                        {inviteRoles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.display_name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={inviteTeam}
                        disabled={busy}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {busy ? "Sending…" : "Send invite"}
                      </button>
                      <button
                        onClick={() => markStep("team", { skipped: true })}
                        className="rounded-md px-4 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      >
                        Skip
                      </button>
                    </div>
                  )}

                  {key === "felix" && (
                    <button
                      onClick={meetFelix}
                      className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                      Say hello to Felix
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* THE "OR IMPORT" HALF OF STEP ONE.
       *
       * The acceptance criteria ask for "create-first-project (or import)", and
       * only the create half existed — a founder migrating from another tool
       * had no route through activation that acknowledged they already have
       * work.
       *
       * ASSUMPTION, STATED BECAUSE THE PHRASE IS AMBIGUOUS: nothing in the
       * product imports a PROJECT. The import surface
       * (/projects/:id/import, RequirementsImport) brings REQUIREMENTS into a
       * project that already exists. So "or import" is offered as seeding the
       * new project from existing requirements rather than starting empty,
       * which is the reading the built machinery actually supports. Inventing a
       * project-import path here would have been a far larger change and a
       * worse one to get wrong.
       *
       * Shown only once a project exists, because that is the first moment the
       * link has somewhere to point.
       */}
      {progress.steps.project?.project_id && (
        <p className="mt-6 text-sm text-slate-600 dark:text-slate-400">
          Bringing work from another tool?{" "}
          <button
            onClick={() => navigate(`/projects/${progress.steps.project?.project_id}/import`)}
            className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
            data-testid="activation-import-requirements"
          >
            Import your existing requirements
          </button>
          .
        </p>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={() => finish(true)}
            data-testid="activation-dismiss"
          className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          I'll do this later
        </button>
        {allDone && (
          <button
            onClick={() => finish(false)}
            className="rounded-md bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700"
            data-testid="activation-finish"
          >
            Go to my workspace
          </button>
        )}
      </div>
    </div>
  );
}

export default ActivationWizard;
